import { useContext, useEffect, useState } from 'react';
import { AdaptivePracticeContext } from '../../../context/context';
import { AssessmentQuestionStatus as QuestionStatus, IAdaptivePracticeContext } from '../interface/interface';
import { FAAngleLeft, FAAngleRight, FACheck, FADelete, FAPaperPlane } from '../../../components/atoms/fontawesomeIcon/Fontawesome';

/**
 * SAVE, and the only thing on the screen that knows whether there is anything to save.
 *
 * It holds that one piece of state itself and the frame hands it a way in, so working in a question
 * lights this button without re-rendering the question - see touchedRef in PlayFrame for what that
 * cost when the frame held it.
 */
function SaveButton(props: {
  bind: { current: ((touched: boolean) => void) | null },
  started: boolean,
  saved: boolean,
  onSave: () => void
}) {
  /* Starts from what the frame already knows. This button is drawn in the place Clear takes on a
     question already answered, so it can be mounted again part-way through a question - and it would
     otherwise come back grey over work that has not been saved. */
  const [touched, setTouched] = useState(props.started);
  const { bind } = props;
  useEffect(() => {
    bind.current = setTouched;
    return () => { bind.current = null; };
  }, [bind]);
  /* Grey says two different things, and only the question knows which: nothing has been answered
     here yet, or what was answered is already in. */
  return (
    <button data-bs-toggle="tooltip"
      title={touched ? 'Keep this answer'
        : props.saved ? 'This answer is already saved'
          : 'Answer the question, then save it'}
      className="nav-act-btn nav-save-btn" disabled={!touched}
      style={!touched ? { opacity: '0.5' } : {}} onClick={props.onSave}>
      <FACheck color='currentColor' size='sm'></FACheck>Save</button>
  );
}

/**
 * THE ROW UNDER THE FRAME on a screen that is handed in: how a student walks the questions, keeps an
 * answer, and finishes.
 *
 * Its own file because it is its own thing. The frame draws a question; this decides what to do with
 * one, and everything belonging to that decision - which button stands where, what each says, when
 * Save is lit - lives here rather than threaded through a screen busy with something else. The frame
 * keeps two lines of it: it holds the answer the question offers (only it can - it is what gives the
 * renderer its context) and it draws this wherever a Submit says there is a sitting.
 *
 * It sits UNDER the frame, mirroring the summary row above it, so the quiz reads the same at the top
 * and the bottom. These controls used to be in the right rail, pinned to its bottom; a question is
 * laid out against the window, so one with long option buttons grew underneath the rail and the two
 * overlapped - the student could not reach Next. Out here the row owns the frame's width and nothing
 * can grow into it.
 *
 * THREE THINGS A STUDENT CAN DO WITH THE QUESTION IN FRONT OF THEM, each saying which it is by its
 * words, its colour and its icon.
 *
 * SAVE (green) takes the answer and only that - they stay where they are, so one act is one act. It
 * is grey until something has been done to the question and goes grey again the moment it is saved,
 * so a lit button always means "there is something here not saved yet". The engine's own Done is
 * hidden while a quiz is being answered - on a long question it sat below the fold, and the answer
 * was lost every time Next was pressed instead - and this is the button that presses it.
 *
 * CLEAR (red) stands in the same place on a question already answered, which comes back as a picture
 * of itself: nothing there can be saved, and the one thing left to do is take the answer back and
 * answer again.
 *
 * NEXT moves on and nothing else; a question left alone is left unanswered, which needs no button of
 * its own. On the LAST question there is nowhere to go, so SUBMIT (yellow) takes its place - and it
 * saves first, because answering and then reaching straight for Submit must not lose the answer.
 *
 * Three slots, so the middle button sits in the middle whether Clear or Save is in it; it is the one
 * a student presses on every question, so it is the widest.
 */
export function QuestionNavFooter(props: {
  /** Hand the answer in. The frame owns this: the renderer registers with IT. */
  onSave: () => void,
  /** How this row tells Save that something has been done to the question since it was last saved. A
   *  channel rather than a prop, so working in a question re-renders one button and not the question
   *  itself - see touchedRef in PlayFrame. */
  bindTouched: { current: ((touched: boolean) => void) | null },
  /** What the frame already knows, for a Save button mounted part-way through a question. */
  touchedAlready: boolean,
  /** Whether this screen keeps the answer until the student saves it. Only the adaptive quiz does;
   *  the assessment answers as it always has, and gets the row it has always had - Prev, Submit once
   *  every question has been seen, Next. */
  saveByHand: boolean
}) {
  const APContext: IAdaptivePracticeContext = useContext(AdaptivePracticeContext);
  const options = APContext.options;
  const strip = options?.questionSummaryHeader;
  const questionStatus = strip?.questionsStatus;
  const isLastQuestion = APContext.isLastQuestion === true;
  const showing = strip?.current ?? 0;

  /** Forward. The last question has nowhere forward to go - it has Submit instead. */
  const moveOn = () => { strip?.setCurrentPos(showing + 1); };

  /**
   * THE ROW AS IT HAS ALWAYS BEEN, for a screen that takes the answer as it is given: there is
   * nothing to save by hand, so there is no Save and no Clear beside it, and Submit waits until
   * every question has been seen rather than standing on the last one.
   */
  if (!props.saveByHand)
    return (
      <div id="question-nav-footer">
        <div className="nav-row d-flex align-items-center justify-content-between">
          <button data-bs-toggle="tooltip" title='Previous question' className="skip-btn"
            disabled={showing <= 0}
            style={showing <= 0 ? { opacity: '0.5' } : {}}
            onClick={() => strip?.setCurrentPos(showing - 1)}>
            <FAAngleLeft color='#fff' size='sm'></FAAngleLeft>Prev</button>

          {(options?.onSubmit && (questionStatus || []).filter(status => status === QuestionStatus.notVisited).length === 0) && (
            <button data-bs-toggle="tooltip" title='Submit your responses' className="submit-btn"
              onClick={options?.onSubmit}>
              Submit<FAPaperPlane color='#fff' size='sm'></FAPaperPlane></button>
          )}

          <button data-bs-toggle="tooltip" title='Next question' className="skip-btn"
            disabled={isLastQuestion}
            style={isLastQuestion ? { opacity: '0.5' } : {}}
            onClick={moveOn}>
            Next<FAAngleRight color='#fff' size='sm'></FAAngleRight></button>
        </div>
      </div>
    );

  return (
    <div id="question-nav-footer">
      <div className="nav-row d-flex align-items-center justify-content-between">
        <div className="nav-slot d-flex align-items-center justify-content-start">
          <button data-bs-toggle="tooltip" title='Previous question' className="skip-btn"
            disabled={showing <= 0}
            style={showing <= 0 ? { opacity: '0.5' } : {}}
            onClick={() => strip?.setCurrentPos(showing - 1)}>
            <FAAngleLeft color='#fff' size='sm'></FAAngleLeft>Prev</button>
        </div>

        <div className="nav-slot nav-slot-wide d-flex align-items-center justify-content-center">
          {(options?.snapShot?.clearSnapShot && options?.snapShot?.innerHTML) ? (
            <button data-bs-toggle="tooltip" title='Clear your response in case you want to change your answer'
              className="nav-act-btn nav-clear-btn" onClick={options?.snapShot?.clearSnapShot}>
              <FADelete color='currentColor' size='sm'></FADelete>Clear</button>
          ) : (
            <SaveButton bind={props.bindTouched} started={props.touchedAlready} onSave={props.onSave}
              saved={questionStatus?.[showing] === QuestionStatus.answered}></SaveButton>
          )}
        </div>

        <div className="nav-slot d-flex align-items-center justify-content-end">
          {(isLastQuestion) ? (
            <button data-bs-toggle="tooltip" title={options?.submitTooltip ?? 'Submit your responses'}
              className="nav-act-btn nav-submit-btn"
              onClick={() => { props.onSave(); options?.onSubmit?.(); }}>
              {options?.submitLabel ?? 'Submit'}<FAPaperPlane color='currentColor' size='sm'></FAPaperPlane></button>
          ) : (
            <button data-bs-toggle="tooltip" title='Next question' className="skip-btn" onClick={moveOn}>
              Next<FAAngleRight color='#fff' size='sm'></FAAngleRight></button>
          )}
        </div>
      </div>
    </div>
  );
}
